import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Send, AlertCircle, FilePlus } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Session } from '@supabase/supabase-js';

interface PointsEarningRequestModalProps {
    session: Session;
    isOpen: boolean;
    onClose: () => void;
    onViewRequests?: () => void;
}

export const PointsEarningRequestModal = ({ session, isOpen, onClose, onViewRequests }: PointsEarningRequestModalProps) => {
    const [provider, setProvider] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [files, setFiles] = useState<FileList | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const onClear = () => {
        setProvider('');
        setEventDate('');
        setAmount('');
        setNotes('');
        setFiles(null);
        setError(null);
    }

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        value = value.replace(/\D/g, "");
        if (!value) {
            setAmount("");
            return;
        }

        const numericValue = parseInt(value, 10) / 100;
        setAmount(numericValue.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL"
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (!files || files.length === 0) {
            setError('Você deve enviar pelo menos um anexo (ex: print da compra).');
            setLoading(false);
            return;
        }

        try {
            // 1. Criar o request na tabela points_earning_requests
            const parsedAmount = amount ? parseFloat(amount.replace(/\D/g, "")) / 100 : null;
            const { data: requestWrapper, error: requestError } = await supabase
                .from('points_earning_requests')
                .insert({
                    provider,
                    event_date: eventDate || null,
                    amount: parsedAmount,
                    currency: 'BRL',
                    notes,
                    status: 'submitted'
                })
                .select()
                .single();

            if (requestError) throw requestError;

            const targetRequestId = requestWrapper.id;

            // 2. Fazer upload dos anexos e salvar na tabela points_earning_attachments
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileExt = file.name.split('.').pop();
                const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
                const filePath = `${session.user.id}/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('points_earning_attachments')
                    .upload(filePath, file);

                if (uploadError) throw uploadError;

                const { error: attachmentError } = await supabase
                    .from('points_earning_attachments')
                    .insert({
                        earning_request_id: targetRequestId,
                        storage_path: filePath
                    });

                if (attachmentError) throw attachmentError;
            }

            setSuccess(true);
            onClear();

        } catch (err: any) {
            console.error('Error submitting points request:', err);
            setError('Falha ao enviar solicitação. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200">

                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400">
                            <FilePlus className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Solicitar Pontos</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">

                    {!success && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-3 rounded-xl border border-blue-100 dark:border-blue-800/30 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <p className="text-sm font-medium">
                                Solicite pontos <strong>após</strong> concluir uma compra. Lembre-se que apenas compras rastreadas pelos nossos links geram pontos.
                            </p>
                        </div>
                    )}

                    {success ? (
                        <div className="text-center p-6 space-y-3">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Send className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Solicitação Enviada!</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Sua solicitação está em análise. Você pode acompanhar o status na página Perfil {'>'} Minhas Solicitações.</p>

                            <div className="flex flex-col sm:flex-row gap-3 mt-6">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        onClear();
                                        setSuccess(false);
                                    }}
                                    className="flex-1"
                                >
                                    Nova Solicitação
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={() => {
                                        if (onViewRequests) {
                                            onViewRequests();
                                        } else {
                                            onClose();
                                        }
                                    }}
                                    className="flex-1"
                                >
                                    Ver Minhas Solicitações
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-1.5">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Loja *
                                </label>
                                <select
                                    className="w-full h-11 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                    value={provider}
                                    onChange={(e) => setProvider(e.target.value)}
                                    required
                                >
                                    <option value="">Selecione...</option>
                                    <option value="mercadolivre">Mercado Livre</option>
                                    <option value="shopee">Shopee</option>
                                    <option value="magalu">Magalu</option>
                                    <option value="aliexpress">AliExpress</option>
                                    <option disabled value="amazon">Amazon (em breve)</option>
                                </select>
                            </div>

                            {(provider === 'shopee' || provider === 'aliexpress') && (
                                <div className="mt-2 mb-4 text-sm text-amber-700 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/10 p-3 rounded-xl border border-amber-200 dark:border-amber-800/30 flex gap-3 animate-in fade-in slide-in-from-top-1">
                                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <p className="leading-snug">
                                        <strong>Dica para agilizar:</strong> Marque o pedido como <strong>"entregue"</strong> na {provider === 'shopee' ? 'Shopee' : 'AliExpress'} antes de enviar esta solicitação. Isso acelera muito o nosso processo de validação!
                                    </p>
                                </div>
                            )}

                            <div className="space-y-1.5">
                                <Input
                                    label="Data da Compra *"
                                    type="date"
                                    value={eventDate}
                                    onChange={(e) => setEventDate(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="space-y-1.5">
                                <Input
                                    label="Valor do produto*"
                                    type="text"
                                    placeholder="R$ 0,00"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    required
                                />
                                <p className="text-[10px] text-gray-500 font-medium leading-tight">
                                    * Não inclua o valor do frete, digite apenas o valor do(s) item(ns).
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Observações
                                </label>
                                <textarea
                                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none h-20"
                                    placeholder="Dica: Inserir o nome dos produtos ajuda a agilizar a validação."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>

                            <div className="space-y-1.5 pt-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Anexos (Obrigatório) *
                                </label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*,.pdf"
                                    onChange={(e) => setFiles(e.target.files)}
                                    className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400 transition-colors"
                                    required
                                />
                                <p className="text-xs text-gray-500 font-medium mt-1.5 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-800">
                                    O comprovante ou tela do pedido deve exibir claramente a <strong className="text-gray-900 dark:text-white">data da compra</strong> e o <strong className="text-gray-900 dark:text-white">valor da compra</strong>. Se precisarmos de mais detalhes, entraremos em contato.
                                </p>
                            </div>

                            {error && (
                                <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/10 p-3 rounded-xl border border-red-100 dark:border-red-900/20">
                                    {error}
                                </div>
                            )}

                            <div className="pt-4 flex gap-3">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={onClose}
                                    className="flex-1"
                                    disabled={loading}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    isLoading={loading}
                                    className="flex-1"
                                >
                                    <Send className="w-4 h-4 mr-2" />
                                    Enviar Solicitação
                                </Button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};
